import { Mutex } from 'async-mutex'

import { getConfiguration } from '../../config'
import { User } from '../../models'
import { TCognitoUserInfo } from '../../types/TCognitoUserInfo'
import { ApiCore } from './ApiCore'
import { NoAuthError, ServerError } from './ApiException'
import { CognitoAuthApi } from './CognitoAuthApi'

const AWS = require('aws-sdk')
const apigClientFactory = require('aws-api-gateway-client').default
const moment = require('moment-timezone')

export class ApiGatewayClient extends ApiCore {
    private readonly awsRegion: string
    private readonly cognitoIdentityPoolId: string
    private apigClient: any
    private readonly mutex: Mutex

    constructor(baseApiUrl: string, apiVersion: string, basePath: string) {
        const config = getConfiguration()
        super({
            baseApiUrl,
            apiVersion,
            basePath
        })

        this.awsRegion = config.awsRegion
        this.cognitoIdentityPoolId = config.cognitoIdentityPoolId
        this.mutex = new Mutex()
    }

    async appendHeaders(): Promise<HeadersInit> {
        return Promise.resolve({})
    }

    private async initApigClient(): Promise<void> {
        const apigClientConfig = {
            accessKey: AWS.config.credentials.accessKeyId,
            secretKey: AWS.config.credentials.secretAccessKey,
            sessionToken: AWS.config.credentials.sessionToken,
            invokeUrl: this.serviceBase,
            region: this.awsRegion
        }

        this.apigClient = apigClientFactory.newClient(apigClientConfig)

        return Promise.resolve()
    }

    private async getAWSCredentials(identityId: string, token: string): Promise<void> {
        AWS.config.region = this.awsRegion
        AWS.config.credentials = new AWS.CognitoIdentityCredentials(
            {
                IdentityPoolId: this.cognitoIdentityPoolId,
                IdentityId: identityId,
                Logins: {
                    'cognito-identity.amazonaws.com': token
                }
            },
            {
                httpOptions: {
                    timeout: 30000
                }
            }
        )

        return new Promise<void>((resolve, reject) => {
            AWS.config.credentials.get((err: any) => (err ? reject(err) : resolve()))
        })
    }

    private async refreshCredentialsForUser(user: User): Promise<void> {
        const cognitoInfo: TCognitoUserInfo = user.getCognitoInfo()
        // @ts-ignore

        return this.refreshCredentials(cognitoInfo.uid, user.username, cognitoInfo.identityId)
    }

    private async refreshCredentials(uid: string, username: string, identityId: string): Promise<void> {
        return new CognitoAuthApi()
            .getToken(uid, username, identityId)
            .then(token => this.getAWSCredentials(identityId, token))
            .then(() => this.initApigClient())
    }

    private async checkAWSCredentials(): Promise<void> {
        return this.mutex.runExclusive(async () => {
            const user: User = new User()
            const currentTime = moment().valueOf()
            const expireTime = AWS.config.credentials !== null ? moment(AWS.config.credentials.expireTime).valueOf() : 0

            if (!user.isAuthorized()) {
                window.parent.postMessage(JSON.stringify({ from: 'WIDGETS', type: 'AUTH_ISSUE' }), '*')

                return Promise.reject({ error: 'AWSCredentialsError' })
            }

            if (
                AWS.config.credentials === null ||
                AWS.config.credentials.expired ||
                expireTime - currentTime < 2 * 60 * 1000
            ) {
                return this.refreshCredentialsForUser(user)
            }

            return this.initApigClient()
        })
    }

    async makeRequest(method: string, path: string, query: any = {}, body: any | undefined): Promise<any> {
        const additionalParams = {
            queryParams: query,
            headers: await this.appendHeaders()
        }

        return this.checkAWSCredentials()
            .then(() => this.apigClient.invokeApi({}, `/${path}`, method, additionalParams, body || {}))
            .then(response => Promise.resolve(response.data))
            .catch(error => {
                if (error.response !== undefined) {
                    const { status } = error.response
                    const text = error.response.statusText
                    const body = error.response.data
                    if (status === 401) {
                        window.parent.postMessage(JSON.stringify({ from: 'WIDGETS', type: 'AUTH_ISSUE' }), '*')
                        throw new NoAuthError()
                    } else if (status === 204) {
                        return this.EmptyResponse
                    } else if (status === 404) {
                        throw new ServerError(body, text)
                    } else if (!status || status < 200 || status >= 300) {
                        throw new ServerError(body, body.error)
                    }
                } else {
                    throw new ServerError({ error: error.message }, error.message)
                }

                return this.EmptyResponse
            })
    }
}
